import type { BotProtectionRuntimeEvent } from './types'

export const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA'

const resolveNodeEnv = (): string => {
  const candidate = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env
    ?.NODE_ENV
  return typeof candidate === 'string' ? candidate : 'development'
}

const toTurnstileSiteKey = (clientConfig: Record<string, unknown>): string | null => {
  const siteKeyRaw = clientConfig.siteKey
  if (typeof siteKeyRaw !== 'string') return null
  const siteKey = siteKeyRaw.trim()
  return siteKey.length > 0 ? siteKey : null
}

const isLocalDevelopmentHostname = (hostnameRaw: string | null | undefined): boolean => {
  const hostname = (hostnameRaw ?? '').trim().toLowerCase()
  if (!hostname) return false
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return true
  if (hostname.endsWith('.local') || hostname.endsWith('.localhost')) return true
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true
  const match172 = hostname.match(/^172\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/)
  if (match172) {
    const second = Number(match172[1])
    if (Number.isFinite(second) && second >= 16 && second <= 31) return true
  }
  return false
}

const resolveTurnstileSiteKey = ({
  configuredSiteKey,
  onRuntimeEvent,
}: {
  configuredSiteKey: string | null
  onRuntimeEvent?: (event: BotProtectionRuntimeEvent) => void
}): string => {
  if (!configuredSiteKey) {
    onRuntimeEvent?.({
      level: 'info',
      event: 'turnstile.dev_test_sitekey_fallback',
      context: { reason: 'missing_site_key' },
    })
    return TURNSTILE_TEST_SITE_KEY
  }

  if (typeof window === 'undefined') return configuredSiteKey
  const hostname = window.location.hostname
  const devMode = resolveNodeEnv() !== 'production'
  const localHostname = isLocalDevelopmentHostname(hostname)
  if (devMode && localHostname && configuredSiteKey !== TURNSTILE_TEST_SITE_KEY) {
    onRuntimeEvent?.({
      level: 'warn',
      event: 'turnstile.dev_test_sitekey_override',
      context: {
        hostname,
        reason: 'local_hostname_with_non_test_key',
      },
    })
    return TURNSTILE_TEST_SITE_KEY
  }

  return configuredSiteKey
}

const toTurnstileChallengeMode = (
  clientConfig: Record<string, unknown>,
): 'managed' | 'invisible' | 'widget' => {
  const challengeMode = clientConfig.challengeMode
  if (challengeMode === 'managed' || challengeMode === 'invisible' || challengeMode === 'widget') {
    return challengeMode
  }
  return 'managed'
}

export type TurnstileWidgetConfig = {
  challengeMode: 'managed' | 'invisible' | 'widget'
  siteKey: string
}

export const resolveTurnstileWidgetConfig = ({
  clientConfig,
  onRuntimeEvent,
}: {
  clientConfig: Record<string, unknown>
  onRuntimeEvent?: (event: BotProtectionRuntimeEvent) => void
}): TurnstileWidgetConfig => ({
  challengeMode: toTurnstileChallengeMode(clientConfig),
  siteKey: resolveTurnstileSiteKey({
    configuredSiteKey: toTurnstileSiteKey(clientConfig),
    onRuntimeEvent,
  }),
})
